import React from 'react';
import { useNavigate } from 'react-router-dom';

const EFFECTIVE_DATE = 'March 1, 2026';
const COMPANY = 'RAMDESIGNWORKS';
const SITE = 'theoperatormethod.com';
const EMAIL = 'admin@theoperatormethod.com';
const PHONE = '(216) 916-6869';
const ADDRESS = '815 Superior Ave. East, Suite 1618, Cleveland, OH 44114';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: '#f0f4ff',
    }}>
      {/* Nav */}
      <div style={{
        padding: '20px 40px',
        borderBottom: '1px solid #1e2a3a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '11px', letterSpacing: '0.25em', fontWeight: 700 }}
        >
          THE OPERATOR METHOD
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: '1px solid #1e2a3a', color: '#6b7a99', cursor: 'pointer', fontSize: '13px', padding: '6px 14px', borderRadius: '6px' }}
        >
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '60px 24px 80px' }}>
        <p style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#4f8ef7', textTransform: 'uppercase', marginBottom: '16px' }}>Legal</p>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 8px', color: '#f0f4ff' }}>Privacy Policy</h1>
        <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '48px' }}>Effective date: {EFFECTIVE_DATE}</p>

        <Section title="1. Who We Are">
          <p>The Operator Method is an online AI training course operated by <strong>{COMPANY}</strong>. This Privacy Policy explains how we collect, use, and protect your personal information when you use our website at <strong>{SITE}</strong>.</p>
          <p>Questions? Contact us at <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a>.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Information you provide directly:</strong></p>
          <ul style={listStyle}>
            <li>Name and email address when you create an account</li>
            <li>Payment information processed through Stripe (we never see or store your card number)</li>
            <li>Course progress and quiz responses as you move through the curriculum</li>
            <li>Feedback you voluntarily submit</li>
          </ul>
          <p><strong>Information collected automatically:</strong></p>
          <ul style={listStyle}>
            <li>Browser type, device type, and operating system</li>
            <li>Pages visited and time spent on the site</li>
            <li>IP address (used for security and fraud prevention only)</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul style={listStyle}>
            <li>To create and manage your account</li>
            <li>To deliver and track your course progress</li>
            <li>To process payments through Stripe</li>
            <li>To send transactional emails (account confirmation, access grants, receipts)</li>
            <li>To respond to your support requests</li>
            <li>To improve the course content and platform</li>
          </ul>
          <p>We do not sell your personal information. We do not use your information for third-party advertising.</p>
        </Section>

        <Section title="4. Payments">
          <p>All payment processing is handled by <strong>Stripe, Inc.</strong> and <strong>PayPal</strong>. When you make a purchase, you are subject to their respective privacy policies. We receive confirmation of your payment and your chosen access tier — we do not receive or store your card number, bank account details, or PayPal credentials.</p>
          <p>Stripe Privacy Policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>stripe.com/privacy</a></p>
        </Section>

        <Section title="5. Data Storage & Security">
          <p>Your account data is stored using <strong>Google Firebase</strong> (Firestore and Firebase Authentication), operated by Google LLC. Firebase is SOC 2 compliant and encrypts data in transit and at rest.</p>
          <p>We implement reasonable security measures to protect your information. No system is 100% secure, and we cannot guarantee absolute security.</p>
          <p><strong>AI Security Enforcement.</strong> All AI features on this platform operate under the <a href="/security-doctrine" style={linkStyle}>AI Security Doctrine, Version 1.0</a>. Security event logs — including detected injection attempts, fingerprint records, and account ban events — are retained indefinitely and may contain excerpts of user-submitted input. All enforcement mechanisms derive from that doctrine.</p>
        </Section>

        <Section title="6. Cookies">
          <p>We use essential cookies and browser local storage to keep you logged in and remember your preferences. We do not use third-party tracking cookies or advertising cookies.</p>
        </Section>

        <Section title="7. Data Retention">
          <p>We retain your account and course progress data for as long as your account is active. If you request deletion of your account, we will remove your personal data within 30 days, except where retention is required by law (e.g., payment records).</p>
        </Section>

        <Section title="8. Your Rights">
          <p>Depending on your location, you may have the right to:</p>
          <ul style={listStyle}>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Object to certain uses of your data</li>
          </ul>
          <p>To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a>. We will respond within 30 days.</p>
          <p><strong>California Residents (CCPA).</strong> If you are a California resident, you have the right to know what personal information we collect about you, to delete that information, and to opt out of its sale. We do not sell personal information. To make a CCPA request, email us at <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a>.</p>
          <p><strong>Canadian Residents (PIPEDA).</strong> If you are a Canadian resident, your personal information is handled in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA). You have the right to access, correct, and request deletion of your personal information. Complaints may be directed to the Office of the Privacy Commissioner of Canada (priv.gc.ca).</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>The Operator Method is intended for users aged 18 and older. We do not knowingly collect personal information from children under the age of 13. If you believe a minor has provided us with personal information, please contact us and we will promptly delete it.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we do, we will update the effective date at the top of this page. Continued use of the site after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact">
          <p>
            {COMPANY}<br />
            {ADDRESS}<br />
            Phone: <a href={`tel:${PHONE.replace(/[^0-9]/g, '')}`} style={linkStyle}>{PHONE}</a><br />
            Email: <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a><br />
            Website: <a href={`https://${SITE}`} style={linkStyle}>{SITE}</a>
          </p>
        </Section>

        <div style={{ borderTop: '1px solid #1e2a3a', paddingTop: '32px', marginTop: '48px', display: 'flex', gap: '24px' }}>
          <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '14px', padding: 0 }}>Terms of Service →</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '40px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#f0f4ff', margin: '0 0 16px', paddingBottom: '8px', borderBottom: '1px solid #1e2a3a' }}>{title}</h2>
      <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '15px' }}>{children}</div>
    </div>
  );
}

const listStyle = { paddingLeft: '20px', lineHeight: 2, margin: '8px 0' };
const linkStyle = { color: '#4f8ef7', textDecoration: 'none' };
